import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Hls from "hls.js";
import { Play, Pause, Volume2, VolumeX, Maximize, Loader2, AlertTriangle, WifiOff, Gauge } from "lucide-react";
import { dataSaverEnabled } from "@/context/SettingsContext";

export const VideoPlayer = ({ src, type, poster, onProgress, startAt = 0 }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const hlsRef = useRef(null);
  const recoverRef = useRef({ net: 0, media: 0 });
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [progress, setProgress] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [dataSaver, setDataSaver] = useState(dataSaverEnabled());
  const isLive = type === "hls";

  const applyDataSaver = (hls) => {
    // Cap to the lowest available rendition to minimise data usage.
    if (dataSaverEnabled() && hls?.levels?.length) {
      hls.autoLevelCapping = 0;
      hls.currentLevel = 0;
    } else if (hls) {
      hls.autoLevelCapping = -1;
    }
  };

  useEffect(() => {
    const onDs = () => setDataSaver(dataSaverEnabled());
    window.addEventListener("ntv-datasaver-change", onDs);
    return () => window.removeEventListener("ntv-datasaver-change", onDs);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    setError(false); setErrMsg(""); setLoading(true); setReconnecting(false);

    if (type === "hls") {
      if (Hls.isSupported()) {
        recoverRef.current = { net: 0, media: 0 };
        const hls = new Hls({
          enableWorker: true,
          manifestLoadingMaxRetry: 6,
          manifestLoadingRetryDelay: 1000,
          levelLoadingMaxRetry: 6,
          fragLoadingMaxRetry: 10,
          fragLoadingRetryDelay: 1000,
          startLevel: dataSaverEnabled() ? 0 : -1,
          capLevelToPlayerSize: dataSaverEnabled(),
        });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setLoading(false); setReconnecting(false);
          applyDataSaver(hls);
          video.play().then(() => setPlaying(true)).catch(() => {});
        });
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (!data.fatal) return;
          const r = recoverRef.current;
          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            // keep retrying network errors; if offline, show reconnecting and wait for 'online'
            if (!navigator.onLine) { setReconnecting(true); return; }
            if (r.net < 6) { r.net += 1; setReconnecting(true); setTimeout(() => hls.startLoad(), 1000); return; }
            setReconnecting(true); // persistent retry loop handled by 'online' listener / reload
            setTimeout(() => { r.net = 0; try { hls.startLoad(); } catch (_) {} }, 4000);
          } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR && r.media < 4) {
            r.media += 1;
            hls.recoverMediaError();
          } else {
            const codec = data.details && String(data.details).toLowerCase().includes("codec");
            setErrMsg(codec ? t("player.formatErrorDesc") : "");
            setError(true);
            setLoading(false);
            try { hls.destroy(); } catch (_) {}
          }
        });
        return () => { try { hls.destroy(); } catch (_) {} hlsRef.current = null; };
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
        video.addEventListener("loadedmetadata", () => { setLoading(false); video.play().catch(() => {}); });
      }
    } else {
      video.src = src;
      const onLoaded = () => {
        setLoading(false);
        if (startAt > 0 && startAt < video.duration) video.currentTime = startAt;
        video.play().then(() => setPlaying(true)).catch(() => {});
      };
      video.addEventListener("loadedmetadata", onLoaded);
      video.addEventListener("error", () => { setError(true); setLoading(false); });
      return () => video.removeEventListener("loadedmetadata", onLoaded);
    }
  }, [src, type, startAt, t]);

  // Auto-reconnect & resume when the connection returns.
  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      const hls = hlsRef.current;
      recoverRef.current.net = 0;
      if (hls) { setReconnecting(true); setError(false); try { hls.startLoad(); } catch (_) {} videoRef.current?.play?.().catch(() => {}); }
      else if (videoRef.current && error) { try { videoRef.current.load(); videoRef.current.play().catch(() => {}); } catch (_) {} }
    };
    const onOffline = () => { setOnline(false); setReconnecting(true); };
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, [error]);

  const reportProgress = useCallback(() => {
    const v = videoRef.current;
    if (v && onProgress && !isLive) onProgress(v.currentTime, v.duration);
  }, [onProgress, isLive]);

  useEffect(() => {
    const id = setInterval(reportProgress, 10000);
    return () => { clearInterval(id); reportProgress(); };
  }, [reportProgress]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => { const v = videoRef.current; v.muted = !v.muted; setMuted(v.muted); };
  const toggleFull = () => {
    if (!document.fullscreenElement) wrapRef.current?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };
  const onTime = () => {
    const v = videoRef.current;
    if (v?.duration) setProgress((v.currentTime / v.duration) * 100);
  };
  const seek = (e) => {
    const v = videoRef.current;
    if (isLive || !v?.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - rect.left) / rect.width) * v.duration;
  };

  return (
    <div ref={wrapRef} data-testid="hls-video-player" className="relative w-full aspect-video bg-black rounded-xl overflow-hidden group">
      <video ref={videoRef} poster={poster} onTimeUpdate={onTime} onClick={togglePlay}
        className="w-full h-full object-contain" playsInline />

      {loading && !error && !reconnecting && (
        <div className="absolute inset-0 grid place-items-center bg-black/60">
          <Loader2 className="animate-spin text-brand" size={40} />
        </div>
      )}

      {(reconnecting || (!online && !error)) && (
        <div data-testid="player-reconnecting" className="absolute inset-0 grid place-items-center bg-black/75 text-center p-6">
          <div>
            <WifiOff className="text-amber-400 mx-auto mb-3 animate-pulse" size={34} />
            <p className="font-semibold mb-1">{online ? t("player.reconnecting") : t("player.waiting")}</p>
            <Loader2 className="animate-spin text-zinc-400 mx-auto mt-2" size={22} />
          </div>
        </div>
      )}

      {error && (
        <div className="absolute inset-0 grid place-items-center bg-black/85 text-center p-6">
          <div>
            <AlertTriangle className="text-gold mx-auto mb-3" size={36} />
            <p className="font-semibold mb-1">{errMsg ? t("player.formatError") : t("player.unavailable")}</p>
            <p className="text-sm text-zinc-400">{errMsg || t("player.unavailableDesc")}</p>
          </div>
        </div>
      )}

      {dataSaver && (
        <div className="absolute top-3 left-3 rtl:left-auto rtl:right-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 text-[11px] font-semibold text-emerald-300">
          <Gauge size={12} /> {t("player.dataSaverOn")}
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        {!isLive && (
          <div data-testid="seek-bar" onClick={seek} className="h-1.5 bg-white/25 rounded-full cursor-pointer mb-3">
            <div className="h-full bg-brand rounded-full" style={{ width: `${progress}%` }} />
          </div>
        )}
        <div className="flex items-center gap-3">
          <button data-testid="player-play" onClick={togglePlay} className="hover:text-brand transition-colors">
            {playing ? <Pause size={22} /> : <Play size={22} fill="currentColor" />}
          </button>
          <button data-testid="player-mute" onClick={toggleMute} className="hover:text-brand transition-colors">
            {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
          </button>
          {isLive && (
            <span className="flex items-center gap-1.5 text-xs font-bold text-brand">
              <span className="w-2 h-2 rounded-full bg-brand animate-pulse" /> {t("player.live")}
            </span>
          )}
          <div className="flex-1" />
          <button data-testid="player-fullscreen" onClick={toggleFull} className="hover:text-brand transition-colors">
            <Maximize size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};
